import { useEffect, useState } from "react";
import { ChatCircleDots, Star, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const categories = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "general", label: "General" },
];

export default function FeedbackDialog({ open, onClose, pagePath }) {
  const [category, setCategory] = useState("general");
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose, submitting]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 10) {
      toast.error("Please add a little more detail.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category,
        rating,
        message: trimmed,
        page_path: pagePath,
      });
      toast.success("Thanks — your feedback was sent.");
      setCategory("general");
      setRating(null);
      setMessage("");
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Feedback could not be sent.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
      <button
        type="button"
        className="feedback-dialog__backdrop"
        onClick={() => !submitting && onClose()}
        aria-label="Close feedback"
      />
      <form className="feedback-dialog__panel" onSubmit={submit}>
        <div className="feedback-dialog__header">
          <span className="feedback-dialog__icon" aria-hidden="true">
            <ChatCircleDots size={21} weight="duotone" />
          </span>
          <div>
            <h2 id="feedback-title">Share feedback</h2>
            <p>Help us improve the Callsect private beta.</p>
          </div>
          <button
            type="button"
            className="feedback-dialog__close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <fieldset className="feedback-dialog__field">
          <legend>What is this about?</legend>
          <div className="feedback-category-options">
            {categories.map((option) => (
              <button
                key={option.value}
                type="button"
                className={category === option.value ? "is-selected" : ""}
                onClick={() => setCategory(option.value)}
                aria-pressed={category === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="feedback-dialog__field">
          <legend>How is your experience? <span>Optional</span></legend>
          <div className="feedback-rating" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRating(rating === value ? null : value)}
                aria-label={`${value} star${value === 1 ? "" : "s"}`}
                aria-pressed={rating === value}
              >
                <Star
                  size={22}
                  weight={rating && value <= rating ? "fill" : "regular"}
                />
              </button>
            ))}
          </div>
        </fieldset>

        <label className="feedback-dialog__field">
          <span>Tell us more</span>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            minLength={10}
            maxLength={2000}
            rows={6}
            placeholder="What happened, or what would make Callsect more useful?"
            autoFocus
            required
          />
          <small>{message.length}/2000</small>
        </label>

        <div className="feedback-dialog__actions">
          <button type="button" className="feedback-secondary-button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="feedback-primary-button" disabled={submitting}>
            {submitting ? "Sending…" : "Send feedback"}
          </button>
        </div>
      </form>
    </div>
  );
}
